import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { changeMemory, deleteMemory, type Memory, type MemoryAction } from "../../api/threads/memoriesApi";
import { appPaths } from "../../shared/routing/appPaths";
import ErrorNotice from "../../errors/ErrorNotice";
import { formatDateTime } from "../../shared/formatting/dateTimes";
import { useSubmission } from "../../shared/state/useSubmission";
import AppButton from "../../ui/buttons/AppButton";
import { memoryActions, passagePreview } from "./memoryActions";
import { notifyMemoriesChanged } from "./memoryEvents";
import MemoryWording from "./steps/MemoryWording";

/**
 * One memory: its wording (editable where the reader may), who kept it, its passages, its own
 * Thread, and what the reader may do. In a Thread's memories and in "My memories".
 * `underReview`: its review step decides, beside it or in its own Thread; the card only shows
 * it and links to that Thread (OW-07).
 */
export default function MemoryRow({ projectId, memory, onChanged, underReview = false }: { projectId: string; memory: Memory; onChanged: () => void; underReview?: boolean }) {
  const { t, i18n } = useTranslation();
  const { state, submit } = useSubmission();
  const busy = state.status === "submitting";
  const act = (action: MemoryAction) => {
    submit(async () => {
      await changeMemory(projectId, memory.memoryId, action);
      onChanged();
      notifyMemoriesChanged();
    });
  };
  const remove = () => {
    submit(async () => {
      await deleteMemory(projectId, memory.memoryId);
      onChanged();
      notifyMemoriesChanged();
    });
  };
  const lastUsed = memory.lastUsedAt === null ? t("memories.neverUsed") : t("memories.lastUsed", { at: formatDateTime(memory.lastUsedAt, i18n.language) });

  return (
    <li className="app-card space-y-2 p-3">
      <MemoryWording projectId={projectId} memory={memory} onChanged={onChanged} readOnly={underReview} />
      <p className="app-text-muted text-xs">
        {t(`memories.state.${memory.state}`)} · {t(`memories.origin.${memory.origin}`)} · {lastUsed} · {t("memories.uses", { count: memory.useCount })}
        {memory.keptIn === null ? null : (
          <>
            {" · "}
            <Link to={appPaths.thread(memory.keptIn)} className="app-link underline">{t("memories.openThread")}</Link>
          </>
        )}
      </p>
      {memory.reason === null ? null : <p className="app-text-muted text-xs">{t("memories.reason", { reason: memory.reason })}</p>}
      <ul className="space-y-1">
        {memory.sources.map((source) => (
          <li key={`${source.entry}:${source.text}`} className="text-sm">
            <q>{passagePreview(source.text)}</q>{" "}
            <Link to={appPaths.thread(source.entry.split("#")[0] ?? "")} className="app-link text-xs underline">
              {source.threadTitle}
            </Link>
          </li>
        ))}
      </ul>
      {underReview ? null : (
        <div className="flex flex-wrap gap-2">
          {memoryActions(memory).map((action) => (
            <AppButton key={action} size="sm" variant={action === "confirm" ? "primary" : "secondary"} disabled={busy} onClick={() => { act(action); }}>
              {t(`memories.action.${action}`)}
            </AppButton>
          ))}
          {memory.mayChange ? (
            <AppButton size="sm" variant="ghost" disabled={busy} onClick={remove}>
              {t("memories.action.delete")}
            </AppButton>
          ) : null}
        </div>
      )}
      {state.status === "failed" ? <ErrorNotice error={state.error} compact={true} /> : null}
    </li>
  );
}
